import { router } from "@eusoft/webapp-framework/services/Router";
import { MaterialIcon } from "@eusoft/webapp-ui/components/Icon";
import { isAuthorize, type IAuthorizeFeature } from "../abstraction/IAuthorizeFeature";
import {navigatePage} from "../actions/Common";
import { HomePage } from "../pages";
import { ListDevicePage } from "../pages/account/ListDevicePage";
import { ListSessionPage } from "../pages/account/ListSessionPage";
import { CreateOrderPage } from "../pages/order/CreateOrderPage";
import { ListOrderPage } from "../pages/order/ListOrderPage";
import { CardDetailsPage } from "../pages/paymentCard/CardDetailsPage";
import { InitCardPage } from "../pages/paymentCard/InitCardPage";
import { ListCardPage } from "../pages/paymentCard/ListCardPage";
import { RechargeCardPage } from "../pages/paymentCard/RechargeCardPage";
import { RefundCardPage } from "../pages/paymentCard/RefundCardPage";
import { ListCatalogPage } from "../pages/product/ListCatalog";
import { ListProductPage } from "../pages/product/ListProduct";
import { OrderReportPage } from "../pages/report/OrderReportPage";
import { WalletReportPage } from "../pages/report/WalletReportPage";
import { SaleCircuitOptionsPage } from "../pages/saleCircuit/SaleCircuitOptionsPage";
import { ListSalePointPage } from "../pages/salePoint/ListSalePoint";
import { hasPermission } from "../services/Authorize";
import { toggleTheme } from "../services/Theme";
import { userSession } from "../services/UserSession";
import type { IContentConstructor, IContentInfo, IContentInstance } from "@eusoft/webapp-ui/abstraction/IContent";
import type { LocalString, ViewNode } from "@eusoft/webapp-ui/types";


export type NavMode = "box" | "list";

export interface INavigationEntry {

    name?: LocalString
    content?: IContentConstructor | IContentInfo;
    color?: string;
    icon?: ViewNode;
    children?: INavigationEntry[];
    navMode?: NavMode;
    action?: () => Promise<unknown>;
    route?: string;
    isVisible?: () => boolean;
}


const CardsNavigation: INavigationEntry[] = [{
    content: InitCardPage,
}, {
    content: CardDetailsPage,
}, {
    content: RechargeCardPage,
}, {
    content: ListCardPage,
}, {
    content: RefundCardPage,
}
]

const ReportsNavigation: INavigationEntry[] = [{
    content: WalletReportPage
},
{
    content: OrderReportPage
}
]

const SettingsNavigation: INavigationEntry[] = [{
    content: SaleCircuitOptionsPage,
},
{
    name: "toggle-theme",
    icon: <MaterialIcon name="contrast" />,
    action: async () => {
        toggleTheme()
    }
}
]

const AccountingNavigation: INavigationEntry[] = [{
    content: ListOrderPage,
}, 
]

const SecurityNavigation: INavigationEntry[] = [{
    content: ListSessionPage,
},{
    content: ListDevicePage,
},
{
    name: "log-out",
    icon: <MaterialIcon color="#f00" name="logout" />,
    action: async () => {
        await userSession.closeAsync();
        navigatePage("login");
    }
},
]

export const BusinessNavigation: INavigationEntry[] = [{
    content: ListSalePointPage,
    color: "#1565C0",
}, {
    content: ListProductPage,
    color: "#00838f",
}, {
    content: CreateOrderPage,
    color: "#FBC02D",
}, {
    content: ListCatalogPage,
    color: "#42A5F5",
}, {
    children: CardsNavigation,
    color: "#6D4C41",
    name: "cards",
    icon: <MaterialIcon name="credit_card" />,
    navMode: "list",
},{
    children: AccountingNavigation,
    color: "#9C27B0",
    name: "accounting",
    icon: <MaterialIcon name="point_of_sale" />,
    navMode: "list",
}, {
    children: SecurityNavigation,
    color: "#F44336",
    name: "security",
    icon: <MaterialIcon name="key" />,
    navMode: "list",
}, {
    children: SettingsNavigation,
    color: "#888",
    name: "settings",
    icon: <MaterialIcon name="settings" />,
    navMode: "list",
}, {
    children: ReportsNavigation,
    color: "#4CAF50",
    name: "reports",
    icon: <MaterialIcon name="bar_chart" />,
    navMode: "list",
}];


export async function processNavAsync(...entries: INavigationEntry[]) {

    for (const entry of entries) {

        const content = entry.content;

        if (!content && !entry.children)
            continue;

        let info: IContentInfo;

        if (typeof content == "object") {

            entry.action = () => router.navigatePageAsync(content.factory());
            entry.icon = content.icon;
            entry.name = content.name;
            info = content;
        }

        else if (typeof content == "function") {
            entry.action = () => navigatePage(content.info.name as any)
            entry.icon = content.info.icon; 
            entry.name = content.info.name as LocalString;  
            info = content.info;
        }
  
        else {

            if (!entry.route)
                entry.route = `/${entry.name}`;

            router.addPage({
                args: { navRoot: entry },
                factory: HomePage.info.factory, 
                route: entry.route,
            } as IContentInstance);

            entry.action = () => router.navigatePageAsync(entry.route);

            if (entry.children) {

                if (!entry.isVisible)
                    entry.isVisible = () => entry.children.some(b => !b.isVisible || b.isVisible());

                await processNavAsync(...entry.children);
            }
        }    

        const auth = info?.features?.find(isAuthorize) as IAuthorizeFeature;

        if (auth?.data) {

            entry.isVisible = () => hasPermission(auth.data);
        }
    }
}

